import { useAuth0 } from '@auth0/auth0-react';
import { Link } from '@tanstack/react-router';
import {
  AlertCircle,
  ExternalLink,
  ArrowLeft,
  FlaskConical,
  MessageSquare,
  Plus,
  Download,
} from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select } from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { API_URL, apiFetch } from '@/config/api';
import { TurnGroup, CopyableId } from '@/features/votes/chat';
import type {
  AnnotationNote,
  CompactionEvent,
  DebugPayloadSkeleton,
  PayloadSection,
} from '@/features/votes/message-votes';
import {
  parseChatHistory,
  groupIntoTurns,
} from '@/features/votes/parseChatHistory';
import { StepReplayEvalDialog } from '@/features/votes/StepReplayEvalDialog';
import {
  findStepReplayTarget,
  INELIGIBILITY_MESSAGE,
} from '@/features/votes/stepReplayTarget';
import {
  useChatHistory,
  useMakeCompactionEval,
  useRunIntrospection,
  useUpdateAnnotation,
} from '@/features/votes/useMessageVotes';
import { useVoteTags, type VoteTagOption } from '@/features/votes/useVoteTags';
import { writeTextToClipboard } from '@/lib/clipboard';

import type { MultiValue } from 'react-select';

const AXIOM_BASE_URL =
  'https://app.axiom.co/little-bird-uv1s/dashboards/kWu7cD7ivgb0rCAfYM';
const AXIOM_OPERATION_NAME = 'POST /chat_v4';
const AXIOM_SERVICE_NAME = 'little-bird';

const STATUS_OPTIONS = ['Pending', 'In Progress', 'Completed'] as const;

function formatExclusionReason(reason: string): string {
  return reason.replace(/_/g, ' ');
}

function buildAxiomTraceUrl(chatId: string = '', email: string = ''): string {
  const params = new URLSearchParams({
    v_chat_id_search: chatId || '',
    v_operation_name: AXIOM_OPERATION_NAME,
    v_service_name: AXIOM_SERVICE_NAME,
    v_status_name: '',
    v_user_email_search: email || '',
  });
  return `${AXIOM_BASE_URL}?${params.toString()}`;
}

interface ChatHistoryPageProps {
  voteId: number;
  isDiscussionOpen: boolean;
  onDiscussionOpenChange: (open: boolean) => void;
  onBack: () => void;
}

type EligibleCompactionEvent = CompactionEvent & {
  turn_number: number;
  durable_record_id: string;
};

export default function ChatHistoryPage({
  voteId,
  isDiscussionOpen,
  onDiscussionOpenChange,
  onBack,
}: ChatHistoryPageProps) {
  // This stamp is the report's updated_at from when introspection was
  // queued, or null when no report existed. It is undefined while no run
  // is awaited.
  const [queuedReportStamp, setQueuedReportStamp] = useState<
    string | null | undefined
  >(undefined);
  const {
    data: chatHistory,
    isLoading,
    error,
  } = useChatHistory(voteId, {
    refetchInterval: (query) =>
      queuedReportStamp !== undefined &&
      (query.state.data?.introspection_report?.updated_at ?? null) ===
        queuedReportStamp
        ? 4000
        : false,
  });
  const updateAnnotation = useUpdateAnnotation(voteId);
  const { data: tagOptions } = useVoteTags();
  const { getAccessTokenSilently, user } = useAuth0();
  const toast = useToast();
  const [exportError, setExportError] = useState<string>('');
  const runIntrospection = useRunIntrospection(voteId);
  const { reset: resetIntrospection } = runIntrospection;
  useEffect(() => {
    resetIntrospection();
    setQueuedReportStamp(undefined);
  }, [voteId, resetIntrospection]);
  const [selectedTags, setSelectedTags] = useState<VoteTagOption[]>([]);
  const [status, setStatus] = useState<string>('');
  const [newNote, setNewNote] = useState<string>('');
  const votedMessageRef = useRef<HTMLDivElement>(null);
  const currentVoteIdRef = useRef(voteId);
  const makeCompactionEval = useMakeCompactionEval(voteId);
  const [isCompactionEvalOpen, setIsCompactionEvalOpen] = useState(false);
  const [evalSummaryDefects, setEvalSummaryDefects] = useState('');
  const [evalCuratorCritique, setEvalCuratorCritique] = useState('');
  const [selectedCompactionTurnNumber, setSelectedCompactionTurnNumber] =
    useState<number | null>(null);
  const { reset: resetCompactionEval } = makeCompactionEval;
  const [isStepReplayEvalOpen, setIsStepReplayEvalOpen] = useState(false);

  const closeCompactionEvalDialog = useCallback(() => {
    setIsCompactionEvalOpen(false);
    setEvalSummaryDefects('');
    setEvalCuratorCritique('');
    setSelectedCompactionTurnNumber(null);
  }, []);

  useEffect(() => {
    currentVoteIdRef.current = voteId;
    resetCompactionEval();
    closeCompactionEvalDialog();
    setIsStepReplayEvalOpen(false);
  }, [voteId, resetCompactionEval, closeCompactionEvalDialog]);

  const isGuardVote = chatHistory?.vote_direction === 1;
  const eligibleCompactionEvents =
    chatHistory?.compaction?.events.filter(
      (compactionEvent): compactionEvent is EligibleCompactionEvent =>
        compactionEvent.eligible_for_replay &&
        compactionEvent.turn_number !== null &&
        compactionEvent.durable_record_id !== null
    ) ?? [];
  const selectedCompactionEvent =
    eligibleCompactionEvents.find(
      (compactionEvent) =>
        compactionEvent.turn_number === selectedCompactionTurnNumber
    ) ?? null;
  const canMakeCompactionEval =
    !!chatHistory?.chat_history_is_shareable &&
    eligibleCompactionEvents.length > 0;

  function openCompactionEvalDialog() {
    setSelectedCompactionTurnNumber(
      eligibleCompactionEvents.length === 1
        ? eligibleCompactionEvents[0].turn_number
        : null
    );
    setIsCompactionEvalOpen(true);
  }

  function handleCreateCompactionEval() {
    if (selectedCompactionTurnNumber === null) return;
    const submittedVoteId = voteId;

    makeCompactionEval.mutate(
      {
        compaction_turn_number: selectedCompactionTurnNumber,
        // the textarea only renders for a repair vote, so a guard vote submits nothing
        summary_defects: evalSummaryDefects.split('\n'),
        curator_critique: evalCuratorCritique.trim(),
      },
      {
        onSuccess: (result) => {
          if (currentVoteIdRef.current !== submittedVoteId) return;
          closeCompactionEvalDialog();
          // a guard case has no friction turn by definition, so its exclusion is not a warning
          const warningDetails = [
            ...(result.friction_eval_exclusion_reason &&
            result.case_kind !== 'regression_guard'
              ? [
                  `Follow-up friction excluded: ${formatExclusionReason(
                    result.friction_eval_exclusion_reason
                  )}`,
                ]
              : []),
            ...result.warnings,
          ].join(' · ');
          toast.success(
            `Compaction eval case ${result.case_id} created`,
            warningDetails || undefined
          );
        },
        onError: (mutationError) => {
          toast.error(
            'Failed to create compaction eval case',
            mutationError instanceof Error ? mutationError.message : undefined
          );
        },
      }
    );
  }

  useEffect(() => {
    if (!chatHistory) return;
    const turnMatch = window.location.hash.match(/^#(\d+)$/);
    // Small delay to ensure ScrollArea is fully rendered
    const timer = setTimeout(() => {
      if (turnMatch) {
        const el = document.getElementById(`turn-${turnMatch[1]}`);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
          return;
        }
      }
      votedMessageRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }, 100);
    return () => clearTimeout(timer);
  }, [chatHistory]);

  useEffect(() => {
    const onHash = () => {
      const m = window.location.hash.match(/^#(\d+)$/);
      if (m) {
        document
          .getElementById(`turn-${m[1]}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (chatHistory?.annotation && tagOptions) {
      const annotation = chatHistory.annotation;
      setStatus(annotation.status || '');
      const tags = annotation.tags
        .map((tag) => tagOptions.find((option) => option.value === tag))
        .filter((option): option is VoteTagOption => option !== undefined);
      setSelectedTags(tags);
    }
  }, [chatHistory, tagOptions]);

  async function handleExportTranscript() {
    try {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/message-votes/${voteId}/transcript`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) throw new Error('Failed to export transcript');
      const text = await res.text();
      const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `downvote-${voteId}-transcript.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(
        e instanceof Error ? e.message : 'Failed to export transcript'
      );
    }
  }

  // Fetched on click and dropped straight into the clipboard. Deliberately not a query hook: a
  // cache key would retain every copied request for the session, which is what we must avoid.
  // Nothing is awaited before the request is handed to the clipboard — see writeTextToClipboard
  // for why awaiting the fetch first makes the browser reject the write.
  function handleCopyPayload(
    messageId: string,
    stepIndex: number
  ): Promise<void> {
    const jsonTextPromise = (async () => {
      const token = await getAccessTokenSilently();
      const res = await apiFetch(
        `${API_URL}/admin/message-votes/${voteId}/payloads/${messageId}/steps/${stepIndex}/export`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.user_error_message || 'Failed to fetch payload'
        );
      }
      return JSON.stringify(await res.json(), null, 2);
    })();

    return writeTextToClipboard(jsonTextPromise).catch(
      async (clipboardError: unknown) => {
        // When the request is what failed, its message is the useful one to show
        const message = await jsonTextPromise.then(
          () =>
            clipboardError instanceof Error
              ? clipboardError.message
              : 'Failed to copy payload',
          (requestError: unknown) =>
            requestError instanceof Error
              ? requestError.message
              : 'Failed to fetch payload'
        );
        toast.error('Failed to copy payload', message);
        throw new Error(message);
      }
    );
  }

  const updateAnnotationField = (updates: {
    status?: string | null;
    tags?: string[];
    notes?: AnnotationNote[];
  }) => {
    updateAnnotation.mutate({
      entity_type: 'vote',
      entity_id: voteId,
      status: updates.status !== undefined ? updates.status : status || null,
      ...(updates.tags !== undefined ? { tags: updates.tags } : {}),
      ...(updates.notes !== undefined ? { notes: updates.notes } : {}),
    });
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    updateAnnotationField({ status: newStatus || null });
  };

  const handleTagsChange = (options: MultiValue<VoteTagOption>) => {
    const newTags = [...options];
    setSelectedTags(newTags);
    updateAnnotationField({ tags: newTags.map((tag) => tag.value) });
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;

    const updatedNotes = [
      ...(chatHistory?.annotation?.notes || []),
      {
        note: newNote.trim(),
        user_email: user?.email || 'unknown',
        time: new Date().toISOString(),
      },
    ];
    setNewNote('');
    updateAnnotationField({ notes: updatedNotes });
  };

  if (error) {
    return (
      <div className="flex h-dvh w-full flex-col bg-background">
        <div className="border-b px-3 py-4 sm:px-6">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Message Votes
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-2 p-8">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto" />
            <p className="text-sm text-destructive font-medium">
              Error Loading Chat History
            </p>
            <p className="text-sm text-muted-foreground">
              {error instanceof Error
                ? error.message
                : 'Failed to load chat history'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-dvh w-full flex-col bg-background">
        <div className="border-b px-3 py-4 sm:px-6">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Message Votes
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!chatHistory) {
    return (
      <div className="flex h-dvh w-full flex-col bg-background">
        <div className="border-b px-3 py-4 sm:px-6">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Message Votes
          </Button>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">No chat history available</p>
        </div>
      </div>
    );
  }

  const canRunIntrospection =
    chatHistory.vote_direction === -1 &&
    (!chatHistory.introspection_report ||
      chatHistory.introspection_report.status === 'skipped');
  const awaitingReport =
    queuedReportStamp !== undefined &&
    (chatHistory.introspection_report?.updated_at ?? null) ===
      queuedReportStamp;
  const stepReplay = findStepReplayTarget(chatHistory);

  return (
    <div className="flex h-dvh w-full flex-col bg-background">
      {/* Header */}
      <div className="shrink-0 border-b px-3 py-4 sm:px-6">
        <div className="space-y-3">
          <Button onClick={onBack} variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Message Votes
          </Button>

          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold mb-2">Chat History</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to="/users/$userId"
                  params={{ userId: chatHistory.user_id.toString() }}
                  search={{ returnTo: null }}
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                >
                  {chatHistory.user_email}
                  {chatHistory.user_name && ` (${chatHistory.user_name})`}
                  <ExternalLink className="h-3 w-3" />
                </Link>
                <p className="text-sm text-muted-foreground inline-flex items-center">
                  {chatHistory.title && `"${chatHistory.title}"`}
                  {chatHistory.chat_id && (
                    <CopyableId
                      value={chatHistory.chat_id}
                      className="ml-2 text-xs"
                    />
                  )}
                </p>
                {chatHistory.chat_id && (
                  <a
                    href={buildAxiomTraceUrl(
                      chatHistory.chat_id,
                      user?.email ?? ''
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1 whitespace-nowrap"
                  >
                    View in Axiom
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="min-w-0 sm:min-w-[150px]">
                <Select
                  value={status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="h-9 text-sm"
                >
                  <option value="">Select status...</option>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="min-w-0 sm:min-w-[220px]">
                <MultiSelectFilter<VoteTagOption>
                  options={tagOptions ?? []}
                  value={selectedTags}
                  onChange={handleTagsChange}
                  placeholder="Select tags..."
                  classNamePrefix="annotation-tags"
                />
              </div>

              <Button
                onClick={() => onDiscussionOpenChange(true)}
                variant="outline"
                size="sm"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                View Discussion
              </Button>
            </div>
          </div>

          {updateAnnotation.isError && (
            <p className="text-sm text-destructive">
              {updateAnnotation.error instanceof Error
                ? updateAnnotation.error.message
                : 'Failed to save annotation'}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="mx-auto max-w-5xl space-y-4 px-3 py-4 sm:space-y-6 sm:px-6 sm:py-6">
            <div className="flex items-center gap-3">
              <Button
                onClick={handleExportTranscript}
                size="sm"
                variant="outline"
              >
                <Download className="mr-1 h-4 w-4" />
                Export Transcript
              </Button>
              <Button
                onClick={openCompactionEvalDialog}
                size="sm"
                variant="outline"
                disabled={!canMakeCompactionEval}
                title={
                  canMakeCompactionEval
                    ? undefined
                    : 'Requires a shared vote with an eligible compaction turn'
                }
              >
                <FlaskConical className="mr-1 h-4 w-4" />
                Make Compaction Eval
              </Button>
              <Button
                onClick={() => setIsStepReplayEvalOpen(true)}
                size="sm"
                variant="outline"
                disabled={stepReplay.target === null}
                title={
                  stepReplay.reason === null
                    ? undefined
                    : INELIGIBILITY_MESSAGE[stepReplay.reason]
                }
              >
                <FlaskConical className="mr-1 h-4 w-4" />
                Make Step Replay Eval
              </Button>
              {canRunIntrospection &&
                (awaitingReport ? (
                  <span className="text-sm text-muted-foreground">
                    Introspection is running. The report will appear here when
                    it is ready.
                  </span>
                ) : (
                  <Button
                    onClick={() =>
                      runIntrospection.mutate(undefined, {
                        onSuccess: () =>
                          setQueuedReportStamp(
                            chatHistory.introspection_report?.updated_at ?? null
                          ),
                      })
                    }
                    disabled={runIntrospection.isPending}
                    size="sm"
                    variant="outline"
                  >
                    {runIntrospection.isPending
                      ? 'Queueing…'
                      : 'Run Introspection'}
                  </Button>
                ))}
              {exportError && (
                <span className="text-sm text-destructive">{exportError}</span>
              )}
              {runIntrospection.isError && (
                <span className="text-sm text-destructive">
                  {runIntrospection.error instanceof Error
                    ? runIntrospection.error.message
                    : 'Failed to queue introspection'}
                </span>
              )}
            </div>
            {chatHistory.vote_reasoning && (
              <div className="space-y-2 rounded-md border bg-muted/30 p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Vote Reasoning
                </div>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {chatHistory.vote_reasoning}
                </div>
              </div>
            )}
            {chatHistory.introspection_report && (
              <div className="space-y-2 rounded-md border bg-muted/30 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Introspection Report
                  {chatHistory.introspection_report.prompt_version && (
                    <span className="rounded bg-muted px-1.5 py-0.5 font-normal normal-case">
                      {chatHistory.introspection_report.prompt_version}
                    </span>
                  )}
                </div>
                {chatHistory.introspection_report.status === 'skipped' ? (
                  <div className="text-sm text-muted-foreground">
                    Skipped
                    {chatHistory.introspection_report.skip_reason &&
                      `: ${chatHistory.introspection_report.skip_reason}`}
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {chatHistory.introspection_report.report_text}
                  </div>
                )}
              </div>
            )}
            {(() => {
              const turns = groupIntoTurns(
                parseChatHistory(chatHistory.chat_history)
              );
              const compactionSummaries: Record<string, string> = {};
              for (const event of chatHistory.compaction?.events ?? []) {
                compactionSummaries[event.trigger_user_message_id] =
                  event.summary;
              }
              const payloadsByMessageId: Record<
                string,
                DebugPayloadSkeleton[]
              > = {};
              for (const payload of chatHistory.payloads) {
                const forMessage =
                  payloadsByMessageId[payload.message_id] ?? [];
                forMessage.push(payload);
                payloadsByMessageId[payload.message_id] = forMessage;
              }
              // Sections carry across turns so a turn's step 0 can be diffed against the
              // previous turn's last request — the usual place a cached prefix breaks. Turns
              // without stored payloads pass the carry-over straight through.
              let carryOverSections: PayloadSection[] = [];
              const turnRows = turns.map((turn) => {
                // Payload skeletons are keyed by the user message that opened the turn
                const turnMessageId = turn.messages.find(
                  (message) => message.role === 'user'
                )?.id;
                const turnPayloads = turnMessageId
                  ? (payloadsByMessageId[turnMessageId] ?? [])
                  : [];
                const previousTurnSections = carryOverSections;
                const lastWithSections = [...turnPayloads]
                  .reverse()
                  .find((payload) => payload.sections.length > 0);
                carryOverSections =
                  lastWithSections?.sections ?? carryOverSections;
                return { turn, turnPayloads, previousTurnSections };
              });
              return turnRows.map(
                ({ turn, turnPayloads, previousTurnSections }) => (
                  <TurnGroup
                    key={turn.turnNumber}
                    turn={turn}
                    voteId={voteId}
                    highlightedFlatIndex={chatHistory.vote_message_index}
                    highlightedMessageRef={votedMessageRef}
                    compactionSummaries={compactionSummaries}
                    systemPrompts={chatHistory.system_prompts}
                    payloads={turnPayloads}
                    previousTurnSections={previousTurnSections}
                    onCopyPayload={handleCopyPayload}
                  />
                )
              );
            })()}
          </div>
        </ScrollArea>
      </div>

      <Dialog
        open={isCompactionEvalOpen}
        onOpenChange={(open) =>
          open ? setIsCompactionEvalOpen(true) : closeCompactionEvalDialog()
        }
      >
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Make Compaction Eval</DialogTitle>
            <DialogDescription>
              Builds a replay eval case from this vote&apos;s stored payloads
              and uploads it to the eval case store. The vote reasoning and
              introspection report are attached automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="compaction-eval-turn"
                className="text-sm font-medium"
              >
                Compaction turn
              </label>
              {eligibleCompactionEvents.length > 0 ? (
                <>
                  <Select
                    id="compaction-eval-turn"
                    value={selectedCompactionTurnNumber?.toString() ?? ''}
                    onChange={(event) =>
                      setSelectedCompactionTurnNumber(
                        event.target.value ? Number(event.target.value) : null
                      )
                    }
                  >
                    <option value="">Select a compaction turn...</option>
                    {eligibleCompactionEvents.map((compactionEvent) => (
                      <option
                        key={compactionEvent.durable_record_id}
                        value={compactionEvent.turn_number}
                      >
                        Turn {compactionEvent.turn_number}
                      </option>
                    ))}
                  </Select>
                  {selectedCompactionEvent && (
                    <div className="max-h-32 overflow-y-auto rounded-md border bg-muted/30 p-3">
                      <p className="mb-1 text-xs font-medium text-muted-foreground">
                        Selected summary
                      </p>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {selectedCompactionEvent.summary}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-dashed bg-muted/30 p-3 text-sm text-muted-foreground">
                  No eligible durable compaction event exists at or before this
                  vote.
                </div>
              )}
            </div>
            {!isGuardVote && (
              <div className="space-y-1.5">
                <label
                  htmlFor="compaction-eval-summary-defects"
                  className="text-sm font-medium"
                >
                  Summary defects (required, one per line)
                </label>
                <Textarea
                  id="compaction-eval-summary-defects"
                  placeholder="Hard, verifiable failure points: wrong facts, hallucinated facts, missing information. One per line."
                  value={evalSummaryDefects}
                  onChange={(e) => setEvalSummaryDefects(e.target.value)}
                  rows={3}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <label
                htmlFor="compaction-eval-critique"
                className="text-sm font-medium"
              >
                Curator critique
              </label>
              <Textarea
                id="compaction-eval-critique"
                placeholder={
                  isGuardVote
                    ? 'Soft notes about this summary, positive or negative — the judge checks whether they still hold in the candidate.'
                    : 'Soft criteria: verbosity, tone, language, structure, general complaints.'
                }
                value={evalCuratorCritique}
                onChange={(e) => setEvalCuratorCritique(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeCompactionEvalDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCompactionEval}
              disabled={
                makeCompactionEval.isPending ||
                selectedCompactionTurnNumber === null ||
                (!isGuardVote && !evalSummaryDefects.trim())
              }
            >
              {makeCompactionEval.isPending ? 'Creating…' : 'Create Case'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {stepReplay.target && (
        <StepReplayEvalDialog
          voteId={voteId}
          target={stepReplay.target}
          voteReasoning={chatHistory.vote_reasoning}
          open={isStepReplayEvalOpen}
          onOpenChange={setIsStepReplayEvalOpen}
        />
      )}

      <Sheet open={isDiscussionOpen} onOpenChange={onDiscussionOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Discussion Notes</SheetTitle>
            <SheetDescription>
              View and add notes for this message vote
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-4">
            {chatHistory?.annotation?.notes &&
            chatHistory.annotation.notes.length > 0 ? (
              <ScrollArea className="h-[calc(100vh-350px)]">
                <div className="space-y-3 pr-4">
                  {chatHistory.annotation.notes.map((note, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-md border bg-muted/50 space-y-1"
                    >
                      <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span>{note.user_email}</span>
                        <span>•</span>
                        <span>
                          {new Date(note.time).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">
                No notes yet. Add one below.
              </p>
            )}

            <div className="space-y-3 pt-4 border-t">
              <Textarea
                placeholder="Write your note here..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                rows={4}
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim() || updateAnnotation.isPending}
                className="w-full"
              >
                <Plus className="h-4 w-4 mr-2" />
                {updateAnnotation.isPending ? 'Adding Note...' : 'Add Note'}
              </Button>
              {updateAnnotation.isError && (
                <p className="text-sm text-destructive text-center">
                  {updateAnnotation.error instanceof Error
                    ? updateAnnotation.error.message
                    : 'Failed to add note'}
                </p>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
